import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import useUserStore from '@/store/userStore';
import AppShellLoader from '@/components/AppShellLoader';
import {
  COMPLETE_PROFILE_PATH,
  getPostAuthPath,
  isOnboardingIncomplete,
  needsPhoneCollection,
} from '@/utils/userAccountStatus';

const AUTH_PATHS = new Set([
  '/auth/login',
  '/auth/signup',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/otp-verification',
]);

const ProtectedRoute = ({ children, requireAuth = true, redirectTo = '/auth/login', allowUnverified = false }) => {
  const { isAuthenticated, user, checkTokenExpiration, _hasHydrated } = useUserStore();
  const [isChecking, setIsChecking] = useState(true);
  const location = useLocation();

  useEffect(() => {
    if (_hasHydrated) {
      const isValid = checkTokenExpiration();
      setIsChecking(false);
      
      if (!isValid && requireAuth) {
        localStorage.setItem('auth_redirect_url', location.pathname + location.search);
      }
    }
  }, [_hasHydrated, checkTokenExpiration, requireAuth, location]);

  if (!_hasHydrated || isChecking) {
    return <AppShellLoader showSidebar={requireAuth} label="Checking session" />;
  }

  if (requireAuth && !isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Only enforce onboarding on protected app routes — never trap users on login/signup.
  if (requireAuth && isAuthenticated && isOnboardingIncomplete(user)) {
    const target = getPostAuthPath(user);
    if (location.pathname !== target) {
      return <Navigate to={target} replace />;
    }
  }

  if (
    requireAuth &&
    isAuthenticated &&
    !needsPhoneCollection(user) &&
    location.pathname === COMPLETE_PROFILE_PATH
  ) {
    return <Navigate to="/home" replace />;
  }

  if (!requireAuth && isAuthenticated) {
    if (!isOnboardingIncomplete(user) && AUTH_PATHS.has(location.pathname)) {
      const redirectUrl = localStorage.getItem('auth_redirect_url') || '/home';
      localStorage.removeItem('auth_redirect_url');
      return <Navigate to={redirectUrl} replace />;
    }

    if (allowUnverified && user) {
      return children;
    }
  }

  return children;
};

export default ProtectedRoute;
